import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';
import { z } from 'zod';
import client from '../client';
import { parseEnvelope } from '../envelope';

const uuidSchema = z.string().uuid();
const nonBlankStringSchema = z.string().refine((value) => value.trim().length > 0, 'must not be blank');

export const linkCandidateRejectRequestSchema = z.object({
  operation_id: uuidSchema,
  evidence_token: nonBlankStringSchema,
}).strict();
export type LinkCandidateRejectRequest = z.infer<typeof linkCandidateRejectRequestSchema>;

export const linkCandidateRejectDataSchema = z.object({
  candidate_id: nonBlankStringSchema,
  batch_id: uuidSchema,
  rejected_at: z.string().datetime({ offset: true }),
}).strict();
export type LinkCandidateRejectData = z.infer<typeof linkCandidateRejectDataSchema>;

export const linkCandidateRejectResponseMetaSchema = z.object({
  idempotent_replay: z.boolean(),
}).strict();
export type LinkCandidateRejectResponseMeta = z.infer<typeof linkCandidateRejectResponseMetaSchema>;

export interface RejectLinkInput {
  candidateId: string;
  evidenceToken: string;
  /** Reuse this identity when deliberately replaying an unknown result. */
  operationId?: string;
}

export interface RejectLinkResult {
  data: LinkCandidateRejectData;
  meta: LinkCandidateRejectResponseMeta;
}

function newOperationId(): string {
  const generated = globalThis.crypto?.randomUUID?.();
  if (!generated) throw new Error('operation ID generation is unavailable');
  return uuidSchema.parse(generated);
}

export function useRejectLink() {
  const queryClient = useQueryClient();
  const operationIdRef = useRef<string | undefined>(undefined);
  const mutation = useMutation<RejectLinkResult, Error, RejectLinkInput>({
    mutationFn: async (input) => {
      const candidateId = nonBlankStringSchema.parse(input.candidateId);
      const body = linkCandidateRejectRequestSchema.parse({
        operation_id: input.operationId ?? newOperationId(),
        evidence_token: input.evidenceToken,
      });
      operationIdRef.current = body.operation_id;
      const response = await client.post(`/api/transactions/link-candidates/${candidateId}/reject`, body);
      const parsed = parseEnvelope(linkCandidateRejectDataSchema, response.data);
      return {
        data: parsed.data,
        meta: linkCandidateRejectResponseMetaSchema.parse(parsed.meta ?? {}),
      };
    },
    retry: false,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inbox'] });
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      queryClient.invalidateQueries({ queryKey: ['transactions', 'link-candidates'] });
    },
  });

  return {
    ...mutation,
    get operationId() {
      return operationIdRef.current;
    },
  };
}

